import React, { useState, useEffect, useRef } from 'react';
import fiscalPrinterService from '../../services/fiscalPrinterService';

const FiscalPrinterButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [printerStatus, setPrinterStatus] = useState({
    connected: false,
    verified: false,  // nowy stan - czy połączenie zweryfikowane
    supported: false
  });
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  const dropdownRef = useRef(null);

  // Sprawdź wsparcie dla Web Serial API + zywy stan polaczenia
  useEffect(() => {
    const supported = fiscalPrinterService.isSupported();
    setPrinterStatus(prev => ({ ...prev, supported }));
    if (!supported) return;

    // Zrodlo prawdy o polaczeniu: monitoring (heartbeat + nasluch USB).
    // Gdy monitoring melduje connected, port jest otwarty i ODPOWIADA na
    // heartbeat -> traktujemy jako zweryfikowany (nie wymagamy recznego
    // "Testuj polaczenie" do zielonego statusu).
    let unsub = () => {};
    if (typeof fiscalPrinterService.subscribeConnection === 'function') {
      unsub = fiscalPrinterService.subscribeConnection((status) => {
        setPrinterStatus(prev => ({
          ...prev,
          connected: !!status.connected,
          verified: !!status.connected,
        }));
      });
    } else {
      const interval = setInterval(() => {
        setPrinterStatus(prev => ({ ...prev, connected: fiscalPrinterService.connected }));
      }, 2000);
      unsub = () => clearInterval(interval);
    }

    return () => unsub();
  }, []);

  // Zamknij dropdown przy kliknięciu poza nim
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Test połączenia - wydzielona funkcja
  const verifyConnection = async () => {
    if (!fiscalPrinterService.connected || !fiscalPrinterService.port) {
      return { success: false, message: 'Port nie jest połączony' };
    }

    if (!fiscalPrinterService.port.writable) {
      return { success: false, message: 'Port nie jest zapisywalny - wybierz inny port' };
    }

    try {
      const status = await fiscalPrinterService.getStatus();

      if (status.connected && status.responding) {
        if (status.online === false) {
          return { success: false, message: 'Drukarka offline - sprawdź czy jest włączona' };
        } else if (status.noPaper) {
          return { success: true, message: 'Drukarka podłączona - UWAGA: brak papieru!', warning: true };
        } else if (status.deviceError) {
          return { success: false, message: 'Drukarka: błąd urządzenia!' };
        } else {
          return { success: true, message: 'Drukarka podłączona i gotowa do pracy!' };
        }
      } else {
        return { 
          success: false, 
          message: 'Drukarka nie odpowiada - wybierz inny port lub sprawdź połączenie'
        };
      }
    } catch (err) {
      return { success: false, message: `Błąd testu: ${err.message}` };
    }
  };

  // Połącz z drukarką - automatyczny test po wyborze portu
  const connectPrinter = async () => {
    try {
      setLoading(true);
      setTestResult(null);
      setLoadingMessage('Wybierz port drukarki...');

      await fiscalPrinterService.connect();

      // Port wybrany - teraz testujemy
      setLoadingMessage('Port wybrany. Testuję połączenie z drukarką...');
      setPrinterStatus(prev => ({ ...prev, connected: true, verified: false }));

      // Automatyczny test połączenia
      const result = await verifyConnection();

      if (result.success) {
        setPrinterStatus(prev => ({ ...prev, verified: true }));
        setTestResult({ 
          success: true, 
          message: result.message,
          warning: result.warning 
        });
      } else {
        // Test nieudany - drukarka nie odpowiada
        setPrinterStatus(prev => ({ ...prev, verified: false }));
        setTestResult({ 
          success: false, 
          message: result.message,
          showRetry: true
        });
      }

    } catch (err) {
      setPrinterStatus(prev => ({ ...prev, connected: false, verified: false }));
      setTestResult({ success: false, message: `Błąd: ${err.message}` });
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Zmień port (rozłącz i połącz ponownie)
  const changePort = async () => {
    try {
      setLoading(true);
      setTestResult(null);
      
      // Rozłącz aktualny
      if (fiscalPrinterService.connected) {
        await fiscalPrinterService.disconnect();
      }
      
      setPrinterStatus(prev => ({ ...prev, connected: false, verified: false }));
      
      // Od razu otwórz wybór portu
      await connectPrinter();
      
    } catch (err) {
      setTestResult({ success: false, message: `Błąd: ${err.message}` });
      setLoading(false);
    }
  };

  // Rozłącz drukarkę
  const disconnectPrinter = async () => {
    try {
      setLoading(true);
      await fiscalPrinterService.disconnect();
      setPrinterStatus(prev => ({ ...prev, connected: false, verified: false }));
      setTestResult({ success: true, message: 'Rozłączono' });
    } catch (err) {
      setTestResult({ success: false, message: `Błąd: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  // Ręczny test połączenia
  const testConnection = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Testuję połączenie...');
      setTestResult(null);

      const result = await verifyConnection();

      if (result.success) {
        setPrinterStatus(prev => ({ ...prev, verified: true }));
      } else {
        setPrinterStatus(prev => ({ ...prev, verified: false }));
      }

      setTestResult({ 
        success: result.success, 
        message: result.message,
        warning: result.warning,
        showRetry: !result.success
      });

    } catch (err) {
      setTestResult({ success: false, message: `Błąd: ${err.message}`, showRetry: true });
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  if (!printerStatus.supported) {
    return null;
  }

  // Status ikony: zielona tylko gdy połączona I zweryfikowana
  const isReady = printerStatus.connected && printerStatus.verified;

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Przycisk główny - CALY w kolorze statusu (zielony=OK / czerwony=rozlaczona) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '0.35rem 0.7rem',
          fontSize: '11px',
          borderRadius: '6px',
          border: `1px solid ${isReady ? 'var(--success)' : printerStatus.connected ? 'var(--warning)' : 'var(--danger)'}`,
          backgroundColor: isReady
            ? 'var(--success)'
            : printerStatus.connected
              ? 'var(--warning)'
              : 'var(--danger)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.35rem',
          fontWeight: '600',
          whiteSpace: 'nowrap',
          transition: 'all 0.15s ease-in-out'
        }}
        title={isReady ? 'Drukarka fiskalna: Gotowa' : printerStatus.connected ? 'Drukarka: wymaga weryfikacji' : 'Drukarka fiskalna: Rozłączona - kliknij aby połączyć'}
      >
        <span style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: '#fff',
          opacity: 0.9,
          flexShrink: 0,
        }} />
        🖨️ {isReady ? 'Drukarka OK' : printerStatus.connected ? 'Sprawdź...' : 'Rozłączona'}
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '4px',
          width: '300px',
          backgroundColor: 'var(--card-bg)',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          zIndex: 9999,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: isReady ? 'var(--success)' : printerStatus.connected ? 'var(--warning)' : 'var(--danger)'
              }} />
              <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text)' }}>
                Drukarka Fiskalna
              </span>
            </div>
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '12px',
              backgroundColor: isReady ? 'var(--success-light)' : printerStatus.connected ? 'var(--warning-light)' : 'var(--danger-light)',
              color: isReady ? 'var(--success)' : printerStatus.connected ? 'var(--warning)' : 'var(--danger)'
            }}>
              {isReady ? 'Gotowa' : printerStatus.connected ? 'Wymaga testu' : 'Niepołączona'}
            </span>
          </div>

          {/* Akcje */}
          <div style={{ padding: '12px' }}>
            {loading ? (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: 'var(--text-muted)'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                <div style={{ fontSize: '13px' }}>{loadingMessage || 'Proszę czekać...'}</div>
              </div>
            ) : !printerStatus.connected ? (
              <button
                onClick={connectPrinter}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid var(--success)', color: 'var(--success)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                🔌 Połącz z drukarką
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {!printerStatus.verified && (
                  <button
                    onClick={testConnection}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      border: '1px solid var(--info)', color: 'var(--info)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    🔍 Testuj połączenie
                  </button>
                )}

                <button
                  onClick={changePort}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    backgroundColor: 'var(--warning)',
                    color: '#333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  🔄 Zmień port
                </button>

                <button
                  onClick={disconnectPrinter}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    border: '1px solid var(--danger)', color: 'var(--danger)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  ⏏️ Rozłącz
                </button>
              </div>
            )}

            {/* Wynik testu */}
            {testResult && !loading && (
              <div style={{
                marginTop: '12px',
                padding: '10px 12px',
                borderRadius: '6px',
                backgroundColor: testResult.success 
                  ? (testResult.warning ? 'var(--warning-light)' : 'var(--success-light)')
                  : 'var(--danger-light)',
                color: testResult.success 
                  ? (testResult.warning ? 'var(--warning)' : 'var(--success)')
                  : 'var(--danger)',
                fontSize: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {testResult.success ? (testResult.warning ? '⚠️' : '✅') : '❌'} {testResult.message}
                </div>
                {testResult.showRetry && (
                  <div style={{ 
                    marginTop: '8px', 
                    paddingTop: '8px', 
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    fontSize: '11px'
                  }}>
                    💡 Kliknij "Zmień port" aby wybrać inny port USB
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Info */}
          <div style={{
            padding: '10px 16px',
            backgroundColor: 'var(--bg)',
            borderTop: '1px solid var(--border)',
            fontSize: '11px',
            color: 'var(--text-muted)'
          }}>
            💡 Pełna konfiguracja: Admin → Drukarka Fiskalna
          </div>
        </div>
      )}
    </div>
  );
};

export default FiscalPrinterButton;
